import {ActionIcon, ThemeIcon, Tooltip} from '@mantine/core';
import type {ActionIconProps, MantineColor} from '@mantine/core';
import {
    IconBan,
    IconCalendar,
    IconCash,
    IconCashOff,
    IconCheck,
    IconKeyOff,
    IconLock,
    IconLogout,
    IconMinus,
    IconPencil,
    IconTrash,
    IconUserCheck,
    IconUserOff,
    IconUserStar,
    IconX,
} from '@tabler/icons-react';
import React from 'react';

type IconTableActionProps = {
    onClick: () => void;
    label: string;
    icon: React.ReactNode;
    color?: MantineColor;
    variant?: ActionIconProps['variant'];
};

function IconTableAction({
    onClick,
    label,
    icon,
    color,
    variant = 'subtle',
}: IconTableActionProps) {
    return (
        <Tooltip label={label} withArrow>
            <ActionIcon
                variant={variant}
                color={color}
                size="sm"
                onClick={onClick}
                aria-label={label}
            >
                {icon}
            </ActionIcon>
        </Tooltip>
    );
}

type EditTableActionProps = {
    onClick: () => void;
    label?: string;
};

export function EditTableAction({onClick, label = 'Edit registration'}: EditTableActionProps) {
    return (
        <IconTableAction
            onClick={onClick}
            label={label}
            color="blue"
            icon={<IconPencil size={16} stroke={1.5}/>}
        />
    );
}

type DeleteTableActionProps = {
    onClick: () => void;
    label?: string;
};

export function DeleteTableAction({onClick, label = 'Delete'}: DeleteTableActionProps) {
    return (
        <Tooltip label={label} withArrow>
            <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                onClick={onClick}
                aria-label={label}
            >
                <IconTrash size={16} stroke={1.5}/>
            </ActionIcon>
        </Tooltip>
    );
}

type PaidToggleActionProps = {
    isPaid: boolean;
    onClick: () => void;
};

type StaffToggleActionProps = {
    isStaff: boolean;
    onClick: () => void;
};

export function StaffToggleAction({isStaff, onClick}: StaffToggleActionProps) {
    const label = isStaff ? 'Remove from staff' : 'Mark as staff';

    return (
        <Tooltip label={label} withArrow>
            <ActionIcon
                variant={isStaff ? 'light' : 'subtle'}
                color={isStaff ? 'violet' : 'gray'}
                size="sm"
                onClick={onClick}
                aria-label={label}
            >
                {isStaff ? (
                    <IconUserStar size={16} stroke={1.5}/>
                ) : (
                    <IconUserOff size={16} stroke={1.5}/>
                )}
            </ActionIcon>
        </Tooltip>
    );
}

export function PaidToggleAction({isPaid, onClick}: PaidToggleActionProps) {
    const label = isPaid ? 'Mark as unpaid' : 'Mark as paid';

    return (
        <Tooltip label={label} withArrow>
            <ActionIcon
                variant={isPaid ? 'light' : 'subtle'}
                color={isPaid ? 'green' : 'orange'}
                size="sm"
                onClick={onClick}
                aria-label={label}
            >
                {isPaid ? (
                    <IconCash size={16} stroke={1.5}/>
                ) : (
                    <IconCashOff size={16} stroke={1.5}/>
                )}
            </ActionIcon>
        </Tooltip>
    );
}

type PaidStatusIconProps = {
    paid: string;
};

export function PaidStatusIcon({paid}: PaidStatusIconProps) {
    const isPaid = paid === 'paid';
    const label = isPaid ? 'Paid' : 'Payment outstanding';

    return (
        <Tooltip label={label} withArrow>
            <ThemeIcon
                color={isPaid ? 'green' : 'orange'}
                variant="light"
                size="sm"
                radius="xl"
                aria-label={label}
            >
                {isPaid ? (
                    <IconCash size={14} stroke={2}/>
                ) : (
                    <IconCashOff size={14} stroke={2}/>
                )}
            </ThemeIcon>
        </Tooltip>
    );
}

export function CurrentUserIndicator() {
    return (
        <Tooltip label="Current user" withArrow>
            <ThemeIcon color="gray" variant="light" size="sm" radius="xl" aria-label="Current user">
                <IconLock size={14} stroke={2}/>
            </ThemeIcon>
        </Tooltip>
    );
}

export function ActiveUntilAction({onClick, label = 'Change active until'}: { onClick: () => void; label?: string }) {
    return (
        <IconTableAction
            onClick={onClick}
            label={label}
            color="gray"
            icon={<IconCalendar size={16} stroke={1.5}/>}
        />
    );
}

type ToggleUserActiveActionProps = {
    isActive: boolean;
    onClick: () => void;
};

export function ToggleUserActiveAction({isActive, onClick}: ToggleUserActiveActionProps) {
    const label = isActive ? 'Deactivate user' : 'Activate user';

    return (
        <IconTableAction
            onClick={onClick}
            label={label}
            color={isActive ? 'red' : 'green'}
            variant={isActive ? 'subtle' : 'light'}
            icon={isActive ? (
                <IconUserOff size={16} stroke={1.5}/>
            ) : (
                <IconUserCheck size={16} stroke={1.5}/>
            )}
        />
    );
}

export function PasskeyResetAction({onClick, label = 'Reset passkey'}: { onClick: () => void; label?: string }) {
    return (
        <IconTableAction
            onClick={onClick}
            label={label}
            color="grape"
            icon={<IconKeyOff size={16} stroke={1.5}/>}
        />
    );
}

export function ChangePasswordAction({onClick, label = 'Change password'}: { onClick: () => void; label?: string }) {
    return (
        <IconTableAction
            onClick={onClick}
            label={label}
            color="orange"
            icon={<IconLock size={16} stroke={1.5}/>}
        />
    );
}

type BooleanStatusIconProps = {
    value: boolean;
    trueLabel?: string;
    falseLabel?: string;
    falseVariant?: 'negative' | 'blocked' | 'neutral';
};

type CheckOutActionProps = {
    onClick: () => void;
};

export function CheckOutAction({onClick}: CheckOutActionProps) {
    return (
        <IconTableAction
            onClick={onClick}
            label="Check out"
            color="orange"
            variant="light"
            icon={<IconLogout size={16} stroke={1.5}/>}
        />
    );
}

export function BooleanStatusIcon({
    value,
    trueLabel = 'Yes',
    falseLabel = 'No',
    falseVariant = 'negative',
}: BooleanStatusIconProps) {
    if (value) {
        return (
            <Tooltip label={trueLabel} withArrow>
                <ThemeIcon color="green" variant="light" size="sm" radius="xl" aria-label={trueLabel}>
                    <IconCheck size={14} stroke={2}/>
                </ThemeIcon>
            </Tooltip>
        );
    }

    if (falseVariant === 'neutral') {
        return (
            <Tooltip label={falseLabel} withArrow>
                <ThemeIcon color="gray" variant="light" size="sm" radius="xl" aria-label={falseLabel}>
                    <IconMinus size={14} stroke={2}/>
                </ThemeIcon>
            </Tooltip>
        );
    }

    const FalseIcon = falseVariant === 'blocked' ? IconBan : IconX;
    const falseColor = falseVariant === 'blocked' ? 'red' : 'gray';

    return (
        <Tooltip label={falseLabel} withArrow>
            <ThemeIcon color={falseColor} variant="light" size="sm" radius="xl" aria-label={falseLabel}>
                <FalseIcon size={14} stroke={2}/>
            </ThemeIcon>
        </Tooltip>
    );
}
